import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import BZAPIService from '../../services/BZAPIService';
import { VendorsContext } from '../../context/VendorsContext';
import { TenantSettingsContext } from '../../context/TenantSettingsContext';
import { buildItems } from './registry';

// Loads every store the Super Admin workspace shows and writes back through the
// endpoints the old page used. Views only see `items` (see buildItems) and call
// apply(item, next); which API a control goes through is decided here.

// What the old page showed when no dashboard row had been saved yet.
const DEFAULT_DASHBOARD_SECTIONS = [
  { label: 'Integrations', value: 'displayIntegration', active: 1 },
  { label: 'Vendor Integrations', value: 'displayVendorIntegration', active: 1 },
  { label: 'Tenant Bug Stats', value: 'displayTenantBugStats', active: 1 },
  { label: 'ODD Bug stats', value: 'displayOddBugStats', active: 1 }
];

const EMPTY_STATE = { tenantSettings: [], vendors: [], dashboardSections: DEFAULT_DASHBOARD_SECTIONS, tasks: [] };
const INITIAL_META = {
  loading: true,
  settingsFailed: false,
  vendorsFailed: false,
  tasksFailed: false,
  appVersion: '',
  tenant: ''
};

// After a task run starts, how often and for how long to re-read the task list for
// its new last-run time. publishedBugCounts takes about 5 s; a queued second run
// waits for the first (reserved concurrency 1).
export const TASK_POLL_MS = 3000;
export const TASK_POLL_FOR_MS = 90000;

const asList = v => (Array.isArray(v) ? v : []);
const flag = on => (on ? 1 : 0);

// Products, features and plain tenant settings all live in a tenantSettings row
// (row.value.value). A product/feature row that was never stored is created on
// first write, so the synthesised "on" row keeps the value it was changed to.
// The row takes the item's type, as the write does: a legacy boolean ODD source
// row becomes a string row when a stage is chosen.
function setRowValue(state, item, value, updatedAt) {
  const patch = row => ({
    ...row,
    value: { ...row.value, value, ...(item.valueType && { type: item.valueType }) },
    ...(updatedAt && { updatedAt })
  });
  const exists = state.tenantSettings.some(r => r.id === item.id);
  return {
    ...state,
    tenantSettings: exists
      ? state.tenantSettings.map(r => (r.id === item.id ? patch(r) : r))
      : [...state.tenantSettings, patch({ id: item.id, value: { type: item.valueType || 'boolean' } })]
  };
}

const mapVendor = (state, id, fn) => ({ ...state, vendors: state.vendors.map(v => (v.id === id ? fn(v) : v)) });
const mapTask = (state, id, fn) => ({ ...state, tasks: state.tasks.map(t => (t.id === id ? fn(t) : t)) });

// The optimistic local change for each source.
const SET_LOCAL = {
  product: setRowValue,
  feature: setRowValue,
  tenantSetting: setRowValue,
  vendor: (state, item, value) => mapVendor(state, item.id, v => ({ ...v, active: flag(value) })),
  bypass: (state, item, value) =>
    mapVendor(state, item.id, v => ({ ...v, vendorData: { ...v.vendorData, bypassStatusIoCheck: value } })),
  dashboard: (state, item, value) => ({
    ...state,
    dashboardSections: state.dashboardSections.map(s => (s.value === item.id ? { ...s, active: flag(value) } : s))
  }),
  // A task's value is "running": on while its start is in flight and while the page
  // waits for the run to land.
  task: (state, item, running) => mapTask(state, item.id, t => ({ ...t, running }))
};

// The request for each source. `state` already holds the optimistic change, which
// is what the dashboard needs: the endpoint takes the whole display array.
const WRITE = {
  product: (item, next) => BZAPIService.updateProductEnablement(item.apiKey, next),
  feature: (item, next) => BZAPIService.updateFeatureEnablement(item.apiKey, next),
  tenantSetting: (item, next) => BZAPIService.updateTenantSetting(item.id, { value: next, type: item.valueType }),
  vendor: (item, next) => BZAPIService.toggleVendor(item.id, next),
  bypass: (item, next) => BZAPIService.updateVendorBypassStatusIo(item.id, next),
  dashboard: (item, next, state) =>
    BZAPIService.postBzDashboard({ type: 'global', bugZeroDashboard: { display: state.dashboardSections } }),
  task: item => BZAPIService.runSuperAdminTask(item.id)
};

const ROW_SOURCES = new Set(['product', 'feature', 'tenantSetting']);

export function useSuperAdminData() {
  const [, setContextVendors] = useContext(VendorsContext);
  const { setBugCoverageEnabled, setProductEnabled } = useContext(TenantSettingsContext);

  // `ref` is the synchronous source of truth, so overlapping writes each build on
  // the latest state; `state` mirrors it for rendering.
  const ref = useRef(EMPTY_STATE);
  const [state, setState] = useState(EMPTY_STATE);
  const [meta, setMeta] = useState(INITIAL_META);
  const alive = useRef(true);
  const taskTimers = useRef(new Map()); // task id -> poll timeout
  const taskWatch = useRef(new Map()); // task id -> the current watch; an older one stops

  const update = useCallback(fn => {
    ref.current = fn(ref.current);
    setState(ref.current);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // GET /super-admin/products is the admin gate and nothing else: the old page
      // called it on load, it is always admin-RBAC checked, and a non-admin's 403 makes
      // BZAPIService's auth monitor redirect to /authentication/forbidden. Its result
      // is ignored; every value on this page comes from the tenantSettings rows.
      const [settings, vendors, dashboard, version, account, tasks] = await Promise.allSettled([
        BZAPIService.getTenantSettings(),
        BZAPIService.getVendors(),
        BZAPIService.getBzDashboard(),
        BZAPIService.getAppVersion(),
        BZAPIService.getAccountInfo(),
        BZAPIService.getSuperAdminTasks(),
        BZAPIService.getProductEnablement()
      ]);
      if (cancelled) return;

      const display = dashboard.status === 'fulfilled' ? dashboard.value?.data?.bugZeroDashboard?.display : null;
      const vendorList = vendors.status === 'fulfilled' ? asList(vendors.value) : [];
      update(() => ({
        tenantSettings: settings.status === 'fulfilled' ? asList(settings.value) : [],
        vendors: vendorList,
        dashboardSections: Array.isArray(display) && display.length ? display : DEFAULT_DASHBOARD_SECTIONS,
        tasks: tasks.status === 'fulfilled' ? asList(tasks.value?.tasks) : []
      }));
      // The navbar and vendor pages read vendors from this context, as they did
      // after the old page's own load.
      if (vendors.status === 'fulfilled') setContextVendors(vendorList);

      setMeta({
        loading: false,
        settingsFailed: settings.status === 'rejected',
        vendorsFailed: vendors.status === 'rejected',
        tasksFailed: tasks.status === 'rejected',
        appVersion: version.status === 'fulfilled' && typeof version.value === 'string' ? version.value : '',
        tenant: account.status === 'fulfilled' && account.value?.tenantId ? String(account.value.tenantId) : ''
      });
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [update, setContextVendors]);

  // After a vendor toggle lands, refetch so the hook and the navbar agree with the
  // server. If only the refetch fails the write still happened: keep the local value
  // and mirror it into the navbar's list instead of rolling back.
  const refreshVendors = useCallback(
    async (id, next) => {
      try {
        const fresh = await BZAPIService.getVendors();
        if (!Array.isArray(fresh)) throw new Error('No vendors returned');
        update(s => ({ ...s, vendors: fresh }));
        setContextVendors(fresh);
      } catch (e) {
        setContextVendors(prev => asList(prev).map(v => (v.id === id ? { ...v, active: flag(next) } : v)));
      }
    },
    [update, setContextVendors]
  );

  useEffect(() => {
    const timers = taskTimers.current;
    alive.current = true; // StrictMode mounts twice
    return () => {
      alive.current = false;
      timers.forEach(clearTimeout);
    };
  }, []);

  // After a run starts, re-read the task list until the task's last run moves past
  // `before`, or TASK_POLL_FOR_MS passes. Either way the row stops reading as running
  // and shows what the server last said. A failed read just tries again. A new run of
  // the same task replaces the watch, even while the old one's read is in flight.
  const watchTask = useCallback(
    (id, before) => {
      clearTimeout(taskTimers.current.get(id));
      const watch = {};
      taskWatch.current.set(id, watch);
      const until = Date.now() + TASK_POLL_FOR_MS;
      const poll = async () => {
        let fresh = null;
        try {
          fresh = asList((await BZAPIService.getSuperAdminTasks())?.tasks).find(t => t.id === id) || null;
        } catch (e) {
          fresh = null;
        }
        if (!alive.current || taskWatch.current.get(id) !== watch) return;
        if ((fresh?.lastRunAt && fresh.lastRunAt !== before) || Date.now() >= until) {
          taskTimers.current.delete(id);
          taskWatch.current.delete(id);
          update(s => mapTask(s, id, t => ({ ...t, ...fresh, running: false })));
          return;
        }
        taskTimers.current.set(id, setTimeout(poll, TASK_POLL_MS));
      };
      taskTimers.current.set(id, setTimeout(poll, TASK_POLL_MS));
    },
    [update]
  );

  // Optimistic: the value changes at once, and goes back if the request is rejected.
  // Resolves true when the write landed, false when it did not. Toasting is the
  // caller's job.
  const apply = useCallback(
    async (item, next) => {
      const setLocal = SET_LOCAL[item.source];
      const write = WRITE[item.source];
      if (!setLocal || !write || item.kind === 'readonly') return false;

      const previous = item.value;
      update(s => setLocal(s, item, next));
      try {
        await write(item, next, ref.current);
      } catch (e) {
        update(s => setLocal(s, item, previous));
        return false;
      }

      if (ROW_SOURCES.has(item.source)) update(s => setRowValue(s, item, next, new Date().toISOString()));
      // A vendor refetch that was read before this write landed may have overwritten the
      // optimistic value while the request was in flight; the write is the newer truth.
      if (item.source === 'bypass') update(s => SET_LOCAL.bypass(s, item, next));
      if (item.source === 'feature' && item.apiKey === 'bugCoverage') setBugCoverageEnabled(next);
      // The shell shows or hides the product at once (STRY0038309), no reload needed.
      if (item.source === 'product' && setProductEnabled) setProductEnabled(item.apiKey, next);
      if (item.source === 'vendor') await refreshVendors(item.id, next);
      if (item.source === 'task') watchTask(item.id, item.lastRunAt);
      return true;
    },
    [update, refreshVendors, setBugCoverageEnabled, setProductEnabled, watchTask]
  );

  const items = useMemo(() => {
    const all = buildItems(state);
    // A product or feature row that is missing reads as its default only when
    // the load worked. After a failed load its state is unknown, so show nothing for it.
    return meta.settingsFailed ? all.filter(i => !i.uid.startsWith('ts:')) : all;
  }, [state, meta.settingsFailed]);

  const failed = [
    meta.settingsFailed && 'tenant settings',
    meta.vendorsFailed && 'vendors',
    meta.tasksFailed && 'tasks'
  ].filter(Boolean);

  return {
    loading: meta.loading,
    loadError: failed.length ? `Couldn't load ${failed.join(' or ')}.` : null,
    items,
    appVersion: meta.appVersion,
    tenant: meta.tenant,
    apply
  };
}
